import React from 'react'
import 'views/views.css'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSquare } from '@fortawesome/free-solid-svg-icons'
import {
    XAxis,
    YAxis,
    Tooltip,
    Line,
    ResponsiveContainer,
    ComposedChart,
    Bar,
} from 'recharts'
import { LoadingIndicator } from 'components'
import { useThemeStyling } from 'hooks/useThemeStyling/useThemeStyling';

/**
 * @name LineBarChart
 * @desc Linehart wrapper
 * @param chartTitle - String that represents the title of the chart
 * @param chartActions - Extended functionality that can exist in the header of the chart container - for example a 'update range' button
 * @param data - Array of data, containing [{name: foo, value: 99},...]
 * @param loading - Bool
 */
const LineBarChart = ({
    data = [],
    loading = false,
    leftYAxisPercent = false,
    rightYAxisPercent = false,
    leftYAxisLabel = '',
    rightYAxisLabel = '',
    barColor = '#b71c1c',
    lineColor = '#60b21d',
}: {
    data: any[]
    loading?: boolean
    leftYAxisPercent?: boolean
    rightYAxisPercent?: boolean
    leftYAxisLabel?: string
    rightYAxisLabel?: string
    barColor?: string
    lineColor?: string
}): React.ReactElement => {
    const { clientTheme } = useThemeStyling()
    
    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <div
                    className={'emp-lineBarChart-tooltipContainer'}
                    style={{ fontSize: clientTheme.typography.body1.fontSize }}
                >
                    <table style={{ borderCollapse: 'collapse' }}>
                        <tbody>
                            <tr style={{ backgroundColor: '#b3b3b3' }}>
                                <td
                                    colSpan={2}
                                    className={
                                        'emp-lineBarChart-tooltipTableHeader'
                                    }
                                >
                                    {label}
                                </td>
                            </tr>
                            <tr>
                                <td
                                    className={
                                        'emp-lineBarChart-tooltipTableRow'
                                    }
                                    style={{
                                        borderRight: '1px dotted #b3b3b3',
                                        textAlign: 'center',
                                    }}
                                >
                                    <FontAwesomeIcon
                                        size="xs"
                                        icon={faSquare}
                                        style={{
                                            marginRight: '5px',
                                            color: lineColor,
                                        }}
                                    />
                                    {leftYAxisLabel}
                                </td>
                                <td
                                    className={
                                        'emp-lineBarChart-tooltipTableRow'
                                    }
                                    style={{
                                        textAlign: 'right',
                                    }}
                                >
                                    {leftYAxisPercent
                                        ? `${payload[0].payload.value.toFixed(
                                              2
                                          )}%`
                                        : payload[0].payload.value}
                                </td>
                            </tr>
                            <tr>
                                <td
                                    className={
                                        'emp-lineBarChart-tooltipTableRow'
                                    }
                                    style={{
                                        borderRight: '1px dotted #b3b3b3',
                                        textAlign: 'center',
                                    }}
                                >
                                    <FontAwesomeIcon
                                        size="xs"
                                        icon={faSquare}
                                        style={{
                                            marginRight: '5px',
                                            color: barColor,
                                        }}
                                    />
                                    {rightYAxisLabel}
                                </td>
                                <td
                                    className={
                                        'emp-lineBarChart-tooltipTableRow'
                                    }
                                    style={{
                                        textAlign: 'right',
                                    }}
                                >
                                    {rightYAxisPercent
                                        ? `${payload[0].payload.count.toFixed(
                                              2
                                          )}%`
                                        : payload[0].payload.count}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )
        }

        return null
    }

    const toPercent = (val: number) => `${val}%`

    return (
        <div className={`emp-lineBarChart-root`}>
            {!loading ? (
                <>
                    {data.length ? (
                        <ResponsiveContainer height={265} width="100%">
                            <ComposedChart
                                data={data}
                                margin={{
                                    top: 20,
                                    left: -10,
                                    bottom: 5,
                                }}
                            >
                                {/* <CartesianGrid strokeDasharray="3 3" /> */}
                                <XAxis
                                    dataKey="date"
                                    angle={90}
                                    textAnchor="start"
                                    height={70}
                                    tick={{
                                        fontSize:
                                            clientTheme.typography.label
                                                .fontSize,
                                    }}
                                />
                                {leftYAxisPercent ? (
                                    <YAxis
                                        tickFormatter={toPercent}
                                        dataKey="value"
                                        label={{
                                            value: leftYAxisLabel,
                                            angle: -90,
                                            position: 'insideLeft',
                                            fontSize: '12px',
                                        }}
                                        yAxisId="left"
                                        tick={{
                                            fontSize:
                                                clientTheme.typography.label
                                                    .fontSize,
                                        }}
                                    />
                                ) : (
                                    <YAxis
                                        dataKey="value"
                                        label={{
                                            value: leftYAxisLabel,
                                            angle: -90,
                                            position: 'insideLeft',
                                            fontSize: '12px',
                                        }}
                                        yAxisId="left"
                                        tick={{
                                            fontSize:
                                                clientTheme.typography.label
                                                    .fontSize,
                                        }}
                                    />
                                )}
                                {rightYAxisPercent ? (
                                    <YAxis
                                        orientation="right"
                                        tickFormatter={toPercent}
                                        dataKey="count"
                                        label={{
                                            value: rightYAxisLabel,
                                            angle: 90,
                                            position: 'insideRight',
                                            fontSize: '12px',
                                        }}
                                        yAxisId="right"
                                        tick={{
                                            fontSize:
                                                clientTheme.typography.label
                                                    .fontSize,
                                        }}
                                    />
                                ) : (
                                    <YAxis
                                        orientation="right"
                                        dataKey="count"
                                        label={{
                                            value: rightYAxisLabel,
                                            angle: 90,
                                            position: 'insideRight',
                                            fontSize: '12px',
                                        }}
                                        yAxisId="right"
                                        tick={{
                                            fontSize:
                                                clientTheme.typography.label
                                                    .fontSize,
                                        }}
                                    />
                                )}
                                <Tooltip content={<CustomTooltip />} />
                                <Bar
                                    dataKey="count"
                                    fill={barColor}
                                    maxBarSize={100}
                                    yAxisId="right"
                                />
                                <Line
                                    dataKey="value"
                                    stroke={lineColor}
                                    fill={lineColor}
                                    yAxisId="left"
                                    dot={{
                                        stroke: lineColor,
                                        fill: lineColor,
                                    }}
                                />
                            </ComposedChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className={'emp-lineBarChart-noDataMessaging'}>
                            No Data
                        </div>
                    )}
                </>
            ) : (
                <div className={'emp-lineBarChart-loadingContainer'}>
                    <LoadingIndicator />
                </div>
            )}
        </div>
    )
}

export default LineBarChart
